import { useCallback, useEffect, useRef, useState } from 'react';
import { getGuildResources } from '@/lib/api-client/guild-resources';
import type { GuildResources } from '@/lib/api-client/guild-resources';
import { ApiError } from '@/lib/api-client/client';

/**
 * Simple, short-lived, guild-scoped in-memory cache - deliberately not a
 * full data-fetching library (task: "do not create a complicated caching
 * framework"). Keyed by guildId so one guild's channels/roles can NEVER
 * be served for another guild's picker, and expires quickly (60s) so a
 * channel created/renamed in Discord shows up again soon without needing
 * a manual refresh mechanism.
 */
const cache = new Map<string, { data: GuildResources; expiresAt: number }>();
const CACHE_TTL_MS = 60_000;

type ResourcesState =
  | { kind: 'loading' }
  | { kind: 'forbidden' }
  | { kind: 'unavailable'; message: string }
  | { kind: 'ready'; data: GuildResources };

/**
 * Shared by every channel/role picker on the page (GuildChannelSelect,
 * GuildRoleSelect) - each one calls this hook for the same `guildId` and
 * gets the same cached response instead of each issuing its own request,
 * which is what keeps a page with several selectors (Welcome: 2 channels
 * + 1 role; Logging: 2 channels) down to one network call per guild visit.
 */
export function useGuildResources(guildId: string | undefined) {
  const [state, setState] = useState<ResourcesState>({ kind: 'loading' });
  // Guards against a slow response for a PREVIOUS guild landing after the
  // user has already switched guilds - same pattern as useGuildConfig/
  // useToggleModule, so a picker never ends up offering guild A's
  // channels while the page shows guild B.
  const requestToken = useRef(0);

  const load = useCallback((force = false) => {
    if (!guildId) return;

    const cached = cache.get(guildId);
    if (!force && cached && cached.expiresAt > Date.now()) {
      setState({ kind: 'ready', data: cached.data });
      return;
    }

    const token = ++requestToken.current;
    setState({ kind: 'loading' });
    getGuildResources(guildId)
      .then((data) => {
        if (requestToken.current !== token) return;
        cache.set(guildId, { data, expiresAt: Date.now() + CACHE_TTL_MS });
        setState({ kind: 'ready', data });
      })
      .catch((err: unknown) => {
        if (requestToken.current !== token) return;
        if (err instanceof ApiError && err.code === 'FORBIDDEN') {
          setState({ kind: 'forbidden' });
        } else if (err instanceof ApiError && (err.code === 'BOT_UNREACHABLE' || err.code === 'BOT_DEGRADED')) {
          setState({ kind: 'unavailable', message: 'Discord data unavailable — the bot is temporarily unreachable.' });
        } else {
          setState({ kind: 'unavailable', message: 'Discord data unavailable — could not load channels/roles.' });
        }
      });
  }, [guildId]);

  // Re-fetches (or re-reads the cache) whenever the guild changes -
  // required so switching guilds never keeps showing the previous
  // guild's channel/role list.
  useEffect(() => {
    load();
  }, [load]);

  return { state, reload: () => load(true) };
}
